import { Formik, FormikHelpers } from "formik";
import { FC, useMemo } from "react";
import Button from "react-bootstrap/Button";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import { Navigate } from "react-router-dom";
import Alert from "react-bootstrap/Alert";
import yup from "app/util/yup";
import { NavigationScreen } from "app/component/screen/NavigationScreen";
import { RouteBreadcrumbs } from "app/route/RouteBreadcrumbs";
import { reportUsersRoute, editReportUserRoute } from "app/route/Routes";
import { useRouteParams } from "app/route/route";
import { ReportRole, useReportUserQuery, useUpdateReportUserMutation } from "app/api/graph/types";
import { reportUserGraphId } from "app/api/graph/helpers";
import { handleError, handleSuccess } from "app/api/graph/form";
import { StatusAlert } from "app/component/form/StatusAlert";
import { FormControlSelectReportRole } from "app/component/form/control/FormControlSelectReportRole";
import { LoadingSpinner } from "app/component/util/LoadingSpinner";
import { AllReportRolePermissionsOverviewCard } from "app/component/report/AllReportRolePermissionsOverviewCard";

type FormValues = {
    role: ReportRole;
};

const schema: yup.ObjectSchema<FormValues> = yup.object({
    role: yup.mixed<ReportRole>().oneOf(Object.values(ReportRole)).required(),
});

export const EditReportUserScreen: FC = () => {
    const { reportId, reportUserId } = useRouteParams(editReportUserRoute);
    const { data, loading, error } = useReportUserQuery({
        variables: { reportUserId: reportUserGraphId(reportUserId) },
    });
    const reportUser = data?.reportUser;
    const [updateReportUser, { data: updateData }] = useUpdateReportUserMutation({});

    const initialValues: FormValues = useMemo(
        () => ({
            role: reportUser?.role ?? ReportRole.Standard,
        }),
        [reportUser],
    );

    if (updateData?.updateReportUser) {
        return <Navigate to={reportUsersRoute.build({ reportId })} />;
    }

    if (loading) {
        return <LoadingSpinner />;
    }
    if (error) {
        return <Alert variant="danger">{error.message}</Alert>;
    }
    if (!reportUser) {
        return <Alert variant="danger">Not found.</Alert>;
    }

    const onSubmit = (values: FormValues, actions: FormikHelpers<FormValues>) =>
        updateReportUser({ variables: { input: { id: reportUserGraphId(reportUserId), role: values.role } } })
            .then(() => handleSuccess(actions))
            .catch((error) => handleError(error, actions))
            .finally(() => actions.setSubmitting(false));

    return (
        <NavigationScreen>
            <Container>
                <RouteBreadcrumbs route={reportUsersRoute} />
                <h1>Update report user</h1>
                <Formik validationSchema={schema} onSubmit={onSubmit} initialValues={initialValues}>
                    {({
                        handleSubmit,
                        setFieldValue,
                        setFieldTouched,
                        values,
                        isSubmitting,
                        errors,
                        touched,
                        status,
                    }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <StatusAlert status={status as unknown} />
                            <Form.Group className="mb-3">
                                <Row>
                                    <Col>
                                        <Form.Label>Role</Form.Label>
                                        <FormControlSelectReportRole
                                            name="role"
                                            value={values.role}
                                            onChange={(value) => setFieldValue("role", value)}
                                            onBlur={() => setFieldTouched("role")}
                                            isInvalid={touched.role && !!errors.role}
                                        />
                                        <Form.Control.Feedback type="invalid">{errors.role}</Form.Control.Feedback>
                                    </Col>
                                    <Col>
                                        <AllReportRolePermissionsOverviewCard />
                                    </Col>
                                </Row>
                            </Form.Group>
                            <Button type="submit" disabled={isSubmitting}>
                                Submit
                            </Button>
                        </Form>
                    )}
                </Formik>
            </Container>
        </NavigationScreen>
    );
};
